import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { AppContext } from '@state/context'
import {
  setCurrentEditBookId,
  setSelectedBookId,
  setSelectedBookTitle,
} from '@state/actions/appActions'
import { openPanel } from '@state/actions/panelsActions'
import {
  PANELS,
  COLORS,
  HEADING_TYPE,
  BUTTON_SIZE,
  BUTTON_TYPE,
  IMAGE_SIZES,
  TEXT_SIZES,
  BOOK_LIST_TYPES,
  USER_ROLES,
} from '@helpers/constants'
import { BookCardStyle } from './style'
import Heading from '@components/Heading'
import Text from '@components/Text'
import Image from '@components/Image'
import Button from '@components/Button'
import Icon from '@components/Icon'
import { useUpdateBook } from '@hooks/mutations/useUpdateBook'
import { getSubjectTextClass } from '@helpers/subject'
import { ImageSize } from '@components/Image/style'
import { getBookImageUrl } from '@helpers/book'
import { useConfirm } from '@hooks/useConfirm'
import withTooltip from '@components/WithTooltip'
import { useUnshareTeachersBook } from '@hooks/mutations/useUnshareTeachersBook'
import { useExamsAllowed } from '@hooks/useExamsAllowed'

const ButtonWithTooltip = withTooltip(Button)

const BookCard = (props) => {
  const {
    book,
    listType,
    isShared = false,
    unShare = null,
    hasQuizExams = false,
  } = props
  const {
    id: bookId,
    imageAlt,
    subject,
    name,
    grade,
    published,
    isHidden,
    schoolYear,
    public: isPublic,
    level: { name: level },
  } = book
  const [state, dispatch] = useContext(AppContext)
  const {
    app: { userId, userRole },
  } = state
  const imageUrl = getBookImageUrl(book)
  const isSaved = book?.customers.some((customer) => customer?.id === userId)
  const subjectColor = getSubjectTextClass(subject?.slug)
  const { post, isPosting } = useUpdateBook()
  const { post: postUnshareBook } = useUnshareTeachersBook()
  const { confirm } = useConfirm()

  const updateBook = async (
    connectCustomers = [],
    disconnectCustomers = []
  ) => {
    if (disconnectCustomers.length > 0) {
      const confirmed = await confirm({
        title: book.name,
        text: 'Är du säker på att du vill ta bort denna bok från din bokhylla? Den försvinner från din bokhylla och dina klasser/grupper, men finns kvar i biblioteket.',
        confirmText: 'Ja, ta bort bok',
        cancelText: 'Avbryt',
      })

      if (!confirmed) return

      const res = await postUnshareBook({
        teacherId: userId,
        bookId,
      })

      if (!res.success) throw 'Kunde inte ta bort boken från användaren'

      await post(
        {
          id: bookId,
          disconnectCustomers,
        },
        'Borttagen från bokhyllan.',
        'Nåt gick fel när boken skulle tas bort från din bokhylla.'
      )
    } else {
      await post(
        {
          id: bookId,
          connectCustomers,
        },
        'Sparad i bokhyllan.',
        'Nåt gick fel när boken skulle sparas i din bokhylla.'
      )
    }
  }

  const examsAllowed = useExamsAllowed()

  return (
    <div className={BookCardStyle.container}>
      <div className='flex items-start justify-between'>
        <div className={'flex text-mediumGrey'}>
          {/* <Icon icon={'Classes'} /> */}
          <Text
            size={TEXT_SIZES.XXXSMALL}
            color={COLORS.MEDIUM_GREY.TEXT}
            // extraClasses={'pl-2'}
          >
            {schoolYear.name}
            <br />
            Nivå: {level}
          </Text>
        </div>
        {/* <Text size={TEXT_SIZES.XXXSMALL} color={COLORS.MEDIUM_GREY.TEXT}>
          Nivå: {level}
        </Text> */}

        {examsAllowed && hasQuizExams && (
          <Icon
            icon={'ExamsBoard'}
            iconClasses='w-[32px] h-[32px]'
            color={COLORS.SECONDARY_GREEN.TEXT}
          />
        )}
      </div>

      <div className='flex pt-6 pb-8 justify-center'>
        <div className={'relative'}>
          <div className='absolute top-[-17px] right-[-10px]'>
            <ButtonWithTooltip
              icon={'Information'}
              type={BUTTON_TYPE.DARK_GREY}
              noMargin
              size={BUTTON_SIZE.ICON_CIRCLE}
              handleClick={() => {
                dispatch(setSelectedBookId(bookId))
                dispatch(openPanel(PANELS.VIEW_BOOK))
              }}
              tooltip={
                book?.creator?.id === userId ? (
                  'Information om boken och möjlighet att radera den.'
                ) : (
                  <span className={'whitespace-nowrap'}>
                    Information om boken.
                  </span>
                )
              }
            />
          </div>
          {imageUrl && (
            <Image
              size={IMAGE_SIZES.LARGE}
              imageUrl={imageUrl}
              altText={imageAlt}
            />
          )}
          {!imageUrl && <div className={ImageSize.large} />}
        </div>
      </div>

      <div className={grade ? BookCardStyle.text : BookCardStyle.smallText}>
        <div className={`${subjectColor} flex`}>
          <Icon icon={'Book'} />
          <Text
            size={TEXT_SIZES.XXXSMALL}
            color={subjectColor}
            extraClasses={'pl-2'}
          >
            {subject?.name}
          </Text>
          {/* {userRole === USER_ROLES.ADMIN && !published && (
                        <Text
                            size={TEXT_SIZES.XXXSMALL}
                            color={COLORS.ALERT_RED.TEXT}
                            extraClasses={'ml-auto'}
                        >
                            UTKAST
                        </Text>
                    )} */}

          {userRole === USER_ROLES.ADMIN && (
            <div className={'ml-auto'}>
              {!published && (
                <Text size={TEXT_SIZES.XXXSMALL} color={COLORS.ALERT_RED.TEXT}>
                  UTKAST
                </Text>
              )}
              {isHidden && (
                <Text size={TEXT_SIZES.XXXSMALL} color={COLORS.SECONDARY_GREEN.TEXT}>
                  GÖMD
                </Text>
              )}
              {isPublic && (
                <Text
                  size={TEXT_SIZES.XXXSMALL}
                  color={COLORS.ALERT_GREEN.TEXT}
                >
                  PUBLIKT
                </Text>
              )}
            </div>
          )}
        </div>
        <div>
          <Heading
            size={TEXT_SIZES.LARGE}
            headingLevel={HEADING_TYPE.H2}
            color={COLORS.WHITE.TEXT}
          >
            {name}
          </Heading>
        </div>
      </div>
      <div className={'flex mt-auto pt-2'}>
        <div className={'pr-3'}>
          <Button
            type={
              listType !== BOOK_LIST_TYPES.LIBRARY
                ? BUTTON_TYPE.SUBJECT
                : BUTTON_TYPE.DARK_GREY
            }
            subject={listType !== BOOK_LIST_TYPES.LIBRARY ? subject.slug : null}
            to={`/bok/${bookId}`}
          >
            Öppna
          </Button>
        </div>
        {listType === BOOK_LIST_TYPES.USER_CREATED && (
          <div className={'pr-3'}>
            <ButtonWithTooltip
              type={BUTTON_TYPE.DARK_GREY}
              size={BUTTON_SIZE.MEDIUM}
              handleClick={() => {
                dispatch(setCurrentEditBookId(book.id))
                dispatch(openPanel(PANELS.EDIT_BOOK))
              }}
              tooltip={'Ändra titel, nivå, avsnittsordning och radera.'}
            >
              {'Redigera'}
            </ButtonWithTooltip>
          </div>
        )}
        {(listType === BOOK_LIST_TYPES.DOKLET ||
          listType === BOOK_LIST_TYPES.LIBRARY) && (
          <div className={'pr-3'}>
            <ButtonWithTooltip
              type={BUTTON_TYPE.DARK_GREY}
              size={BUTTON_SIZE.MEDIUM}
              disabled={
                isPosting || (isSaved && listType === BOOK_LIST_TYPES.LIBRARY)
              }
              handleClick={() => {
                !isSaved
                  ? updateBook([userId], []) // connect
                  : updateBook([], [userId]) // disconnect
              }}
              icon={
                isSaved && listType !== BOOK_LIST_TYPES.LIBRARY
                  ? 'Exit'
                  : 'Favourite'
              }
              iconRight
              tooltip={
                isSaved
                  ? listType === BOOK_LIST_TYPES.LIBRARY
                    ? 'Redan sparad i Min bokhylla.'
                    : 'Ta bort boken från din bokhylla. Boken försvinner från din bokhylla och dina klasser/grupper, men finns kvar i biblioteket.'
                  : 'Spara boken i Min bokhylla.'
              }
            >
              {isSaved
                ? listType === BOOK_LIST_TYPES.LIBRARY
                  ? 'Sparad'
                  : 'Ta bort'
                : 'Spara'}
            </ButtonWithTooltip>
          </div>
        )}
        {!isShared && (
          <div>
            <ButtonWithTooltip
              type={
                listType === BOOK_LIST_TYPES.LIBRARY
                  ? BUTTON_TYPE.SUBJECT
                  : BUTTON_TYPE.DARK_GREY
              }
              subject={
                listType === BOOK_LIST_TYPES.LIBRARY ? subject.slug : null
              }
              noMargin
              size={BUTTON_SIZE.ICON_CIRCLE}
              handleClick={() => {
                dispatch(setSelectedBookId(bookId))
                dispatch(setSelectedBookTitle(name))
                dispatch(openPanel(PANELS.SHARE_BOOK))
              }}
              icon={'Share'}
              tooltip={'Dela boken med en klass/grupp.'}
            />
          </div>
        )}

        {isShared && (
          <div>
            <Button
              type={BUTTON_TYPE.DARK_GREY}
              size={BUTTON_SIZE.ICON_CIRCLE}
              handleClick={() => {
                if (unShare) {
                  unShare()
                }
              }}
              icon={'Exit'}
            />
          </div>
        )}
      </div>
      {grade && (
        <div className={BookCardStyle.lastChild}>
          <Text size={TEXT_SIZES.XXSMALL} bold>
            {grade}
          </Text>
        </div>
      )}
    </div>
  )
}

BookCard.propTypes = {
  book: PropTypes.object,
  listType: PropTypes.string,
  size: PropTypes.string,
  isShared: PropTypes.bool,
  unShare: PropTypes.func,
  hasQuizExams: PropTypes.bool,
}

export default BookCard
